import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import {
  ChevronLeft,
  ShieldCheck,
  Sparkles,
  Building2,
  KeyRound,
  Layers,
  Cpu,
  ArrowRight,
  CheckCircle2,
} from 'lucide-react'

const readiness = {
  available: { label: 'Available', className: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  pilot: { label: 'Pilot', className: 'bg-ember-500/15 text-ember-300 border-ember-500/30' },
  building: { label: 'Building', className: 'bg-signal-500/15 text-signal-300 border-signal-500/30' },
}

const audiences = [
  {
    icon: Sparkles,
    label: 'New to AI',
    title: "Never used an AI product before? Start here.",
    description:
      "Graffiti™ OS is the software. 23Controls™ is the accountability layer underneath it — it means every AI action traces back to a real, verified person, not an anonymous account. You don't have to understand the governance to benefit from it; it's on by default.",
    cta: 'See the Graffiti™ Scrambler',
    href: '/products/home',
    gradient: 'from-synth-400 to-signal-500',
  },
  {
    icon: Cpu,
    label: 'Early Adopter',
    title: 'Want to see it running, not just read about it?',
    description:
      "Account creation, identity binding, and image generation are live today. Flash an SD card, boot it on a Raspberry Pi 5-class unit, and you're running governed AI — this is demonstrable, not a roadmap slide.",
    cta: 'Explore RAG & multi-model workflows',
    href: '/products/enterprise/rag-workflows',
    gradient: 'from-purple-500 to-synth-600',
  },
  {
    icon: Building2,
    label: 'Enterprise Architect',
    title: 'Evaluating this for an organization?',
    description:
      'A compliance rulebook (PCI-DSS, HIPAA, PII, CCPA/CPRA) runs at ingest time with one canonical, six-year audit log. We score readiness per component and say plainly what is live, what is pilot, and what is still being built — not one blanket claim.',
    cta: 'See the Compliance page',
    href: '/corporate/legal/compliance',
    gradient: 'from-ember-500 to-synth-700',
  },
]

const stack = [
  { name: '23Controls™', role: 'Governance & accountability', detail: 'Identity Anchor, compliance rulebook, audit trail, human-in-the-loop review, agent policy.' },
  { name: 'Graffiti™ OS', role: 'Local enclave runtime', detail: 'Containerized services, encrypted evidence storage, local vector search, local model runtime.' },
  { name: 'Conductor', role: 'Orchestration', detail: 'Workflows that carry a task through ingest, enrichment, human review, and delivery.' },
  { name: 'Scrambler', role: 'Edge hardware', detail: 'The physical unit Graffiti™ OS runs on — optional if you only need the software.' },
]

const capabilities = [
  { name: 'Governed data ingest & local vector search', status: 'available' },
  { name: 'Compliance rulebook & audit trail (13 live rules)', status: 'available' },
  { name: 'Account creation, identity binding, image generation', status: 'available' },
  { name: 'Device registration & full-stack first boot', status: 'pilot' },
  { name: 'Entity resolution with human review', status: 'pilot' },
  { name: 'Multi-step Conductor workflows', status: 'building' },
]

export default function GovernancePage() {
  return (
    <div className="pt-24 pb-16">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link
          to="/products"
          className="inline-flex items-center gap-2 text-void-400 hover:text-void-200 transition-colors mb-8"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to Products
        </Link>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-synth-900/50 border border-synth-700/50 text-synth-300 text-sm font-medium mb-6">
            <ShieldCheck className="w-4 h-4" />
            23Controls™
          </span>
          <h1 className="text-4xl md:text-5xl font-display font-bold text-void-50 mb-6 max-w-3xl">
            Governed AI, from the first action.
          </h1>
          <p className="text-xl text-void-400 max-w-2xl mb-16 leading-relaxed">
            23Controls™ is the governance layer every Graffiti™ OS unit runs under: an accountable
            identity behind every unit, a compliance rulebook that runs at ingest time, and a
            human in the loop for anything ambiguous. This is the layer, not a hardware product —
            the Scrambler is one way to run it, not the whole story.
          </p>

          <h2 className="text-2xl font-display font-bold text-void-50 mb-6">Find your starting point</h2>
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {audiences.map((a, i) => (
              <motion.div
                key={a.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }}
                className="flex flex-col h-full p-6 rounded-2xl bg-void-900/30 border border-void-800/50"
              >
                <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${a.gradient} mb-4 w-fit`}>
                  <a.icon className="w-5 h-5 text-white" />
                </div>
                <p className="text-xs font-semibold text-synth-400 uppercase tracking-wider mb-2">{a.label}</p>
                <h3 className="text-lg font-display font-semibold text-void-100 mb-3">{a.title}</h3>
                <p className="text-void-400 text-sm leading-relaxed mb-6 flex-1">{a.description}</p>
                <Link
                  to={a.href}
                  className="inline-flex items-center gap-2 text-synth-400 hover:text-synth-300 font-medium text-sm transition-colors"
                >
                  {a.cta} <ArrowRight className="w-4 h-4" />
                </Link>
              </motion.div>
            ))}
          </div>

          <h2 className="text-2xl font-display font-bold text-void-50 mb-6">The stack</h2>
          <div className="grid sm:grid-cols-2 gap-4 mb-16">
            {stack.map((s) => (
              <div key={s.name} className="p-5 rounded-xl bg-void-900/30 border border-void-800/50">
                <div className="flex items-baseline gap-2 mb-1">
                  <h3 className="font-display font-semibold text-void-100">{s.name}</h3>
                  <span className="text-xs text-void-500">{s.role}</span>
                </div>
                <p className="text-void-400 text-sm leading-relaxed">{s.detail}</p>
              </div>
            ))}
          </div>

          <h2 className="text-2xl font-display font-bold text-void-50 mb-4 flex items-center gap-3">
            <KeyRound className="w-6 h-6 text-synth-400" />
            Identity Anchor
          </h2>
          <p className="text-void-300 leading-relaxed mb-16 max-w-3xl">
            Every participant across 23Controls™ — every unit, every license, every subscription —
            traces back to one verified Identity Anchor representing a single real person. It's
            what keeps automated actions accountable: no anchor, no machine. We're not publishing
            the internals of how the Anchor is implemented; this page describes what it does for
            you, not how it's built.
          </p>

          <h2 className="text-2xl font-display font-bold text-void-50 mb-6 flex items-center gap-3">
            <Layers className="w-6 h-6 text-synth-400" />
            Where things actually stand
          </h2>
          <div className="rounded-2xl border border-void-800/50 overflow-hidden mb-6">
            {capabilities.map((c, i) => (
              <div
                key={c.name}
                className={`flex items-center justify-between gap-4 p-4 ${i !== 0 ? 'border-t border-void-800/50' : ''} bg-void-900/30`}
              >
                <span className="text-void-300 text-sm">{c.name}</span>
                <span className={`inline-flex items-center px-3 py-1 rounded-full border text-xs font-medium shrink-0 ${readiness[c.status].className}`}>
                  {readiness[c.status].label}
                </span>
              </div>
            ))}
          </div>
          <p className="text-void-500 text-sm max-w-2xl mb-16">
            Readiness levels are founder-assessed per component, not independent attestations. We
            don't claim SOC 2, HITRUST, or similar certifications we haven't obtained.
          </p>

          <div className="rounded-2xl bg-void-900/30 border border-void-800/50 p-8">
            <h2 className="text-2xl font-display font-bold text-void-50 mb-4">Ready to see it running?</h2>
            <ul className="space-y-2 mb-6">
              {[
                'Create an account — email verification, MFA',
                'Attest an ID to create your Identity Anchor',
                'Your image is generated and deployed',
                'Flash it to an SD card, boot it, and it appears on the network',
              ].map((step, i) => (
                <li key={step} className="flex items-start gap-2 text-void-300 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-synth-500 shrink-0 mt-0.5" />
                  {step}
                </li>
              ))}
            </ul>
            <a
              href="https://23controls.com/signup"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-synth-600 hover:bg-synth-500 text-white font-medium transition-colors"
            >
              Get Started <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  )
}
